import { supabase } from './supabase';
import type { ProspectQueryResult, PropertyPin } from '../types';
import type { FilterState } from '../constants';

export interface ProspectQueryArgs {
  polygon?: GeoJSON.Polygon | null;
  postcodes?: string[];
  filters: FilterState;
  sampleSize?: number;
}

// Cached result of "is the properties table empty?" — avoids re-checking on
// every keystroke. Reset to null to re-test (e.g. after ingestion runs).
let propertiesTableEmpty: boolean | null = null;

async function isPropertiesTableEmpty(): Promise<boolean> {
  if (propertiesTableEmpty !== null) return propertiesTableEmpty;
  try {
    const { count, error } = await supabase
      .from('doorsteps_properties')
      .select('uprn', { count: 'exact', head: true });
    if (error) {
      // RLS / missing-table / RPC error — treat as empty so we show demo data.
      propertiesTableEmpty = true;
    } else {
      propertiesTableEmpty = (count ?? 0) === 0;
    }
  } catch {
    propertiesTableEmpty = true;
  }
  return propertiesTableEmpty;
}

export async function queryProperties(args: ProspectQueryArgs): Promise<ProspectQueryResult> {
  const { polygon, postcodes, filters, sampleSize = 500 } = args;

  const { data, error } = await supabase.rpc('doorsteps_query_properties', {
    p_polygon: polygon ?? null,
    p_postcodes: postcodes && postcodes.length ? postcodes : null,
    p_min_ownership: filters.min_ownership ?? null,
    p_min_epc_age: filters.min_epc_age ?? null,
    p_property_types: filters.property_types?.length ? filters.property_types : null,
    p_min_price: filters.min_price ?? null,
    p_max_price: filters.max_price ?? null,
    p_min_score: filters.min_score ?? null,
    p_sample_size: sampleSize,
  });

  if (error) {
    console.error('queryProperties error', error);
    return { ...mockProspectResult(filters, polygon, postcodes), is_demo: true };
  }

  const result: ProspectQueryResult = {
    total: data?.total ?? 0,
    sample: (data?.sample ?? []) as PropertyPin[],
  };

  // If the table is empty (no ingestion has run yet) fall back to demo data so
  // the agent can still try the create-campaign flow.
  if (result.total === 0 && (await isPropertiesTableEmpty())) {
    return { ...mockProspectResult(filters, polygon, postcodes), is_demo: true };
  }

  return result;
}

/**
 * Used when Supabase isn't configured or the properties table is empty,
 * so the Prospect screen still has something to render during local dev.
 *
 * Pins are scattered inside the polygon (if drawn) or postcode-bbox so the
 * map looks like the user's selection actually contains properties.
 */
function mockProspectResult(
  filters: FilterState,
  polygon?: GeoJSON.Polygon | null,
  postcodes?: string[]
): ProspectQueryResult {
  const bbox = polygonBbox(polygon) ?? defaultBbox(postcodes);
  const sample: PropertyPin[] = [];
  for (let i = 0; i < 120; i++) {
    const score = 30 + Math.round(Math.random() * 70);
    if (filters.min_score && score < filters.min_score) continue;
    if (filters.min_ownership && filters.min_ownership > 25) continue;
    sample.push({
      uprn: `mock_${i}`,
      address_line_1: `${i + 1} Sample Lane`,
      postcode: postcodes?.[0] ?? ['CM1 1AB', 'SS1 2CD', 'CT2 3EF'][i % 3],
      property_type: (['detached', 'semi', 'terraced', 'flat'] as const)[i % 4],
      lng: bbox.minLng + Math.random() * (bbox.maxLng - bbox.minLng),
      lat: bbox.minLat + Math.random() * (bbox.maxLat - bbox.minLat),
      move_likelihood: score,
      ownership_years: 3 + Math.floor(Math.random() * 18),
      epc_age_years: 2 + Math.floor(Math.random() * 14),
      last_sale_price: 220000 + Math.floor(Math.random() * 600000),
    });
  }
  return { total: sample.length, sample };
}

interface Bbox { minLng: number; maxLng: number; minLat: number; maxLat: number }
function polygonBbox(p?: GeoJSON.Polygon | null): Bbox | null {
  if (!p?.coordinates?.[0]?.length) return null;
  const ring = p.coordinates[0];
  const lngs = ring.map((c) => c[0]);
  const lats = ring.map((c) => c[1]);
  return {
    minLng: Math.min(...lngs),
    maxLng: Math.max(...lngs),
    minLat: Math.min(...lats),
    maxLat: Math.max(...lats),
  };
}
function defaultBbox(_postcodes?: string[]): Bbox {
  // Roughly centred on Essex / Greater London / Kent.
  return { minLng: 0.2, maxLng: 0.6, minLat: 51.4, maxLat: 51.7 };
}

export async function getPropertyByUprn(uprn: string) {
  const { data, error } = await supabase
    .from('doorsteps_properties')
    .select('*')
    .eq('uprn', uprn)
    .maybeSingle();
  if (error) return null;
  return data;
}
